import { useState } from "react";
import { ChevronRight } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { RichTextEditor } from "@/components/RichTextEditor";
import { NotesSection } from "@/components/notes/NotesSection";
import { cn } from "@/lib/utils";

interface ItemBriefingTabProps {
  briefing: string;
  onBriefingChange: (briefing: string) => void;
  /** The item's id — undefined in create mode, which hides the notes list. */
  itemId?: string;
}

/**
 * Briefing tab for the item editor: a collapsible Briefing section (the item's
 * editable markdown pre-read) above the item's notes, mirroring the Notes
 * section's heading hierarchy. Briefing edits save with the sheet's Save
 * button.
 */
export function ItemBriefingTab({
  briefing,
  onBriefingChange,
  itemId,
}: ItemBriefingTabProps) {
  const [isBriefingOpen, setIsBriefingOpen] = useState(true);

  return (
    <div className="h-full w-full overflow-y-auto p-4">
      <Collapsible
        open={isBriefingOpen}
        onOpenChange={setIsBriefingOpen}
        className="pb-6"
      >
        <CollapsibleTrigger className="flex items-center gap-2 w-full group cursor-pointer select-none mb-4">
          <ChevronRight
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform duration-200",
              isBriefingOpen && "rotate-90",
            )}
          />
          <h3 className="text-lg font-semibold">Briefing</h3>
        </CollapsibleTrigger>
        <CollapsibleContent className="animate-collapsible-down data-[state=closed]:animate-collapsible-up">
          <RichTextEditor
            value={briefing}
            onChange={onBriefingChange}
            placeholder="Notes, key decisions, and open judgement calls"
            className="min-h-[240px]"
          />
        </CollapsibleContent>
      </Collapsible>
      {itemId && <NotesSection entityType="ITEM" entityId={itemId} />}
    </div>
  );
}
